import { User, Route, Event, Attachment, ChecklistItem, Collaboration } from '../types';

// Автоматическое определение API URL в зависимости от домена
const getApiBaseUrl = (): string => {
  // Сначала проверяем переменную окружения (приоритет)
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  const hostname = window.location.hostname;
  
  // Если домен содержит .local, используем localhost
  if (hostname.includes('.local')) {
    return 'http://localhost:8000/api';
  }
  
  // Если домен содержит planmytrip.mytools.quest, используем production API
  if (hostname.includes('planmytrip.mytools.quest')) {
    return 'https://api.planmytrip.mytools.quest/api';
  }
  
  // По умолчанию используем localhost для разработки
  return 'http://localhost:8000/api';
};

const API_BASE_URL = getApiBaseUrl();

// Логируем используемый API URL для отладки
console.log('Current hostname:', window.location.hostname);
console.log('Using API URL:', API_BASE_URL);

// Типы для API ответов
interface AuthResponse {
  access: string;
  refresh: string;
}

interface LoginData {
  email: string;
  password: string;
}

interface RegisterData {
  username: string;
  email: string;
  password: string;
  password_confirm: string;
  first_name?: string;
  last_name?: string;
}

// Базовый класс для API
class ApiService {
  private baseURL: string;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    const token = localStorage.getItem('access_token');

    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        if (response.status === 401) {
          // Попытка обновить токен
          const refreshed = await this.refreshToken();
          if (refreshed) {
            // Повторяем запрос с новым токеном
            const newToken = localStorage.getItem('access_token');
            config.headers = {
              ...config.headers,
              Authorization: `Bearer ${newToken}`,
            };
            const retryResponse = await fetch(url, config);
            if (!retryResponse.ok) {
              throw new Error(`HTTP error! status: ${retryResponse.status}`);
            }
            return await retryResponse.json();
          }
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  private async refreshToken(): Promise<boolean> {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) return false;

    try {
      const response = await fetch(`${this.baseURL}/token/refresh/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh: refreshToken }),
      });

      if (response.ok) {
        const data: AuthResponse = await response.json();
        localStorage.setItem('access_token', data.access);
        return true;
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
    }

    // Если обновление не удалось, очищаем токены
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    return false;
  }

  // Аутентификация
  async login(data: LoginData): Promise<AuthResponse> {
    return this.request<AuthResponse>('/token/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async register(data: RegisterData): Promise<User> {
    return this.request<User>('/users/register/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getCurrentUser(): Promise<User> {
    return this.request<User>('/users/me/');
  }

  // Маршруты
  async getRoutes(params?: Record<string, any>): Promise<Route[]> {
    const searchParams = new URLSearchParams(params);
    return this.request<Route[]>(`/routes/?${searchParams}`);
  }

  async getRoute(id: number): Promise<Route> {
    return this.request<Route>(`/routes/${id}/`);
  }

  async createRoute(data: Partial<Route>): Promise<Route> {
    return this.request<Route>('/routes/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateRoute(id: number, data: Partial<Route>): Promise<Route> {
    return this.request<Route>(`/routes/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteRoute(id: number): Promise<void> {
    return this.request<void>(`/routes/${id}/`, {
      method: 'DELETE',
    });
  }

  async copyRoute(id: number): Promise<Route> {
    return this.request<Route>(`/routes/${id}/copy/`, {
      method: 'POST',
    });
  }

  async shiftDates(id: number, daysShift: number): Promise<Route> {
    return this.request<Route>(`/routes/${id}/shift-dates/`, {
      method: 'POST',
      body: JSON.stringify({ days_shift: daysShift }),
    });
  }

  // События
  async getEvents(dayId: number): Promise<Event[]> {
    return this.request<Event[]>(`/events/days/${dayId}/events/`);
  }

  async getEvent(id: number): Promise<Event> {
    return this.request<Event>(`/events/events/${id}/`);
  }

  async createEvent(dayId: number, data: Partial<Event>): Promise<Event> {
    return this.request<Event>(`/events/days/${dayId}/events/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateEvent(id: number, data: Partial<Event>): Promise<Event> {
    return this.request<Event>(`/events/events/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteEvent(id: number): Promise<void> {
    return this.request<void>(`/events/events/${id}/`, {
      method: 'DELETE',
    });
  }

  async reorderEvents(dayId: number, eventOrders: Array<{ event_id: number; order: number }>): Promise<Event[]> {
    return this.request<Event[]>(`/events/days/${dayId}/events/reorder/`, {
      method: 'POST',
      body: JSON.stringify({ event_orders: eventOrders }),
    });
  }

  // Вложения
  async getAttachments(eventId: number): Promise<Attachment[]> {
    return this.request<Attachment[]>(`/events/events/${eventId}/attachments/`);
  }

  async createAttachment(eventId: number, data: FormData): Promise<Attachment> {
    const token = localStorage.getItem('access_token');
    const response = await fetch(`${this.baseURL}/events/events/${eventId}/attachments/`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: data,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  async deleteAttachment(id: number): Promise<void> {
    return this.request<void>(`/events/attachments/${id}/`, {
      method: 'DELETE',
    });
  }

  // Списки
  async getChecklistItems(routeId: number, type?: string): Promise<ChecklistItem[]> {
    const searchParams = new URLSearchParams();
    if (type) {
      searchParams.append('type', type);
    }
    return this.request<ChecklistItem[]>(`/checklists/routes/${routeId}/items/?${searchParams}`);
  }

  async createChecklistItem(routeId: number, data: Partial<ChecklistItem>): Promise<ChecklistItem> {
    return this.request<ChecklistItem>(`/checklists/routes/${routeId}/items/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateChecklistItem(id: number, data: Partial<ChecklistItem>): Promise<ChecklistItem> {
    return this.request<ChecklistItem>(`/checklists/items/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteChecklistItem(id: number): Promise<void> {
    return this.request<void>(`/checklists/items/${id}/`, {
      method: 'DELETE',
    });
  }

  async toggleChecklistItem(id: number): Promise<ChecklistItem> {
    return this.request<ChecklistItem>(`/checklists/items/${id}/toggle/`, {
      method: 'POST',
    });
  }

  async reorderChecklistItems(routeId: number, itemOrders: Array<{ item_id: number; order: number }>): Promise<ChecklistItem[]> {
    return this.request<ChecklistItem[]>(`/checklists/routes/${routeId}/items/reorder/`, {
      method: 'POST',
      body: JSON.stringify({ item_orders: itemOrders }),
    });
  }

  async moveToEvents(id: number, dayId: number): Promise<Event> {
    return this.request<Event>(`/checklists/items/${id}/move-to-events/`, {
      method: 'POST',
      body: JSON.stringify({ day_id: dayId }),
    });
  }

  // Совместная работа
  async getCollaborations(routeId: number): Promise<Collaboration[]> {
    return this.request<Collaboration[]>(`/collaboration/routes/${routeId}/invitations/`);
  }

  async createCollaboration(routeId: number, data: Partial<Collaboration>): Promise<Collaboration> {
    return this.request<Collaboration>(`/collaboration/routes/${routeId}/invitations/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteCollaboration(id: number): Promise<void> {
    return this.request<void>(`/collaboration/invitations/${id}/`, {
      method: 'DELETE',
    });
  }

  async getMyInvitations(): Promise<Collaboration[]> {
    return this.request<Collaboration[]>('/collaboration/my-invitations/');
  }

  async acceptInvitation(id: number): Promise<Collaboration> {
    return this.request<Collaboration>(`/collaboration/invitations/${id}/accept/`, {
      method: 'POST',
    });
  }

  async getSharedRoutes(): Promise<Route[]> {
    return this.request<Route[]>('/collaboration/shared-routes/');
  }
}

export const apiService = new ApiService(API_BASE_URL); 